import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { apiPost } from "../../shared/apiClient";

function formatRoleLabel(role) {
  if (!role) return "Administrator";
  return role.toLowerCase() === "admin" ? "Administrator" : role;
}

function friendlyLoginError(raw) {
  const text = String(raw || "").trim();
  if (!text) return "Invalid username or password.";
  if (/failed to fetch|network error|econnrefused|load failed/i.test(text)) {
    return "We're having trouble connecting. Please try again.";
  }
  if (/exception|stack trace|sql/i.test(text)) {
    return "Unable to sign in. Please try again.";
  }
  try {
    const parsed = JSON.parse(text);
    return parsed.message || parsed.Message || "Invalid username or password.";
  } catch {
    return text.length < 180 ? text : "Unable to sign in. Please try again.";
  }
}

function pick(obj, ...keys) {
  for (const key of keys) {
    if (obj != null && obj[key] != null) return obj[key];
  }
  return undefined;
}

function LoginVectorIllustration() {
  return (
    <svg viewBox="0 0 560 520" className="login-vector" aria-hidden>
      <g className="login-vector__bubble login-vector__bubble--a">
        <circle cx="110" cy="94" r="26" className="login-vector__line" />
        <path d="M96 94h28M110 80v28" className="login-vector__line" />
      </g>
      <g className="login-vector__bubble login-vector__bubble--b">
        <rect x="390" y="74" width="90" height="56" rx="14" className="login-vector__line" />
        <path d="M412 102h46" className="login-vector__line" />
      </g>

      <rect x="72" y="162" width="196" height="238" rx="18" className="login-vector__phone" />
      <rect x="88" y="182" width="164" height="178" rx="12" className="login-vector__screen-light" />
      <rect x="108" y="202" width="124" height="16" rx="8" className="login-vector__line-fill" />
      <rect x="108" y="228" width="94" height="12" rx="6" className="login-vector__line-fill-soft" />
      <rect x="108" y="248" width="134" height="12" rx="6" className="login-vector__line-fill-soft" />
      <rect x="108" y="278" width="58" height="58" rx="10" className="login-vector__tile" />
      <rect x="176" y="278" width="58" height="58" rx="10" className="login-vector__tile" />
      <path d="M140 171h60" className="login-vector__line" />

      <g className="login-vector__store">
        <rect x="304" y="176" width="184" height="162" rx="18" className="login-vector__store-body" />
        <path d="M304 206h184" className="login-vector__line" />
        <path d="M318 176h156l-10 30H328z" className="login-vector__awning" />
        <rect x="330" y="234" width="48" height="74" rx="8" className="login-vector__door" />
        <rect x="392" y="234" width="74" height="44" rx="8" className="login-vector__window" />
      </g>

      <g className="login-vector__truck">
        <rect x="286" y="352" width="132" height="54" rx="12" className="login-vector__truck-body" />
        <rect x="418" y="366" width="48" height="40" rx="8" className="login-vector__truck-cabin" />
        <circle cx="322" cy="414" r="14" className="login-vector__wheel" />
        <circle cx="438" cy="414" r="14" className="login-vector__wheel" />
      </g>

      <g className="login-vector__bag">
        <path d="M468 314h48l-6 72h-36z" className="login-vector__bag-fill" />
        <path d="M480 314c0-10 7-18 12-18s12 8 12 18" className="login-vector__line" />
      </g>
    </svg>
  );
}

export function LoginPage() {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const navigate = useNavigate();

  if (auth.user) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const apiRes = await apiPost("/api/Authentication/login", { userName, password }, { feedback: false });

      if (!apiRes.ok) {
        throw new Error(friendlyLoginError(apiRes.error));
      }

      const data = apiRes.data ?? {};
      const role = pick(data, "role", "Role") ?? "Admin";
      const sessionUser = {
        id: pick(data, "userId", "UserId") ?? 0,
        userName: pick(data, "userName", "UserName") ?? userName,
        role,
        displayName: formatRoleLabel(role),
        fullName: formatRoleLabel(role)
      };

      auth.login(
        sessionUser,
        pick(data, "token", "Token") ?? "",
        pick(data, "expiresAt", "ExpiresAt") ?? null
      );
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Login failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page--login">
      <div className="auth-layout">
        <section className="auth-panel auth-panel--visual">
          <div className="auth-visual-content">
            <img src={`${process.env.PUBLIC_URL}/images/admin.png`} alt="Admin Dashboard" className="auth-illustration" />
            <h2 className="auth-visual-title">Manage. Monitor. Grow.</h2>
            <p className="auth-visual-desc">
              Your complete e-commerce command centre — products, orders, and customers in one place.
            </p>
          </div>
          <div className="auth-visual-dots" aria-hidden />
        </section>

        <section className="auth-panel auth-panel--form">
          <div className="auth-form-inner">
            <div className="auth-logo-row">
              <img src={`${process.env.PUBLIC_URL}/images/mainlogo.png`} alt="OnlineX" className="auth-logo" />
            </div>

            <h1>Welcome back</h1>
            <p className="auth-subtitle">Sign in to your admin panel</p>

            <form onSubmit={handleSubmit} className="auth-form auth-form--modern">
              <label className="field field--auth">
                <span className="field__label">Username</span>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 0114 0H3z" />
                  </svg>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    autoComplete="username"
                    placeholder="Enter your username"
                    className="input--auth input--auth-icon"
                  />
                </div>
              </label>

              <label className="field field--auth">
                <span className="field__label">Password</span>
                <div className="auth-password-wrap">
                  <svg className="auth-input-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="input--auth input--auth-icon"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </label>

              {error && (
                <div className="error-banner error-banner--auth" role="alert">
                  {error}
                </div>
              )}

              <button className="btn btn-primary btn-auth-submit" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="btn-auth-spinner" aria-hidden />
                    Signing in...
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            <p className="auth-powered-by">
              Powered by <a href="https://ainsoftsolutions.com" target="_blank" rel="noreferrer">ainsoftsolutions.com</a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
